import DOMPurify from "dompurify";
import type { GlobalState } from "@/types/globalState";
import type { ContentItemRecord } from "@/types/contentItemRecord";
import type { LocalizedContentItem } from "@/types/localizedContentItem";

import parse, {
    domToReact,
    attributesToProps,
    type DOMNode,
    type Element,
    type HTMLReactParserOptions,
} from "html-react-parser";

export async function getContentitems(
    filename: string,
    globalState: GlobalState,
    updateGlobalState: (patch: Partial<GlobalState>) => void
): Promise<ContentItemRecord[]> {
    try {
        const language = globalState.Language;

        const response = await fetch("/contentitem/getcontentitems", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename, language }),
        });

        let parsed: unknown = await response.json();


        // Verwacht: array
        if (!Array.isArray(parsed)) {
            parsed = [];
        }

        // Als er een foutstructuur terugkomt
        if (
            (parsed as any[]).length > 0 &&
            (parsed as any[])[0]?.code !== undefined &&
            (parsed as any[])[0]?.code === "error"
        ) {
            parsed = [];
        }

        updateGlobalState({ 
            Contentitems: parsed as ContentItemRecord[],
            LoadedContentFiles: [filename] // Reset loaded files for new page
        });

        const items = parsed as ContentItemRecord[];
        return items;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("getContentitems failed:", err);
        return [];
    }
}

export async function getGlobalContent(
    globalState: GlobalState,
    updateGlobalState: (patch: Partial<GlobalState>) => void
): Promise<ContentItemRecord[]> {
    try {
        const language = globalState.Language;

        const response = await fetch("/contentitem/getglobalcontent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename: "AcademyGlobalContent.xml", language }),
        });

        let globalitems: unknown = await response.json();

        if (!Array.isArray(globalitems)) {
            globalitems = [];
        }

        if (
            (globalitems as any[]).length > 0 &&
            (globalitems as any[])[0]?.code !== undefined &&
            (globalitems as any[])[0]?.code === "error"
        ) {
            globalitems = [];
        }

        updateGlobalState({ Globalcontent: globalitems as ContentItemRecord[] });

        const items = globalitems as ContentItemRecord[];
        return items;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("getGlobalContent failed:", err);
        return [];
    }
}

/** Haalt 1 item uit Contentitems (op basis van huidige taal) en sanitize't content. */
export function getContentitem(
    id: string,
    globalState: GlobalState
): LocalizedContentItem {
    try {
        const language = globalState.Language;
        const contentitems = globalState.Contentitems ?? [];

        for (let i = 0; i < contentitems.length; i++) {
            const localized = contentitems[i]?.[language];
            if (localized && localized.name === id) {
                const sanitized = DOMPurify.sanitize(localized.content, {
                    USE_PROFILES: { html: true },
                });
                // We muteren niet het originele object; returnen een kopie
                return { ...localized, content: sanitized };
            }
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("getContentitem failed:", err);
    }

    // Fallback leeg item
    return {
        content: "",
        contentlabel: "",
        language: "",
        name: "",
        publicationStartdate: "0001-01-01T00:00:00",
        publicationEnddate: "0001-01-01T00:00:00",
        title: "",
        titleLabel: "",
    };
}

/**
 * Merge additional content items into existing Contentitems.
 * If the content file was already loaded, skip the reload.
 * Otherwise, fetch and merge the new items (overwriting duplicates by name).
 */
export async function mergeContentitems(
    filename: string,
    globalState: GlobalState,
    updateGlobalState: (patch: Partial<GlobalState>) => void
): Promise<ContentItemRecord[]> {
    try {
        // Check if this file was already loaded
        const loadedFiles = globalState.LoadedContentFiles || [];
        if (loadedFiles.includes(filename)) {
            // Already loaded, return existing items
            return globalState.Contentitems || [];
        }

        const language = globalState.Language;

        const response = await fetch("/contentitem/getcontentitems", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename, language }),
        });

        let parsed: unknown = await response.json();

        // Verwacht: array
        if (!Array.isArray(parsed)) {
            parsed = [];
        }

        // Als er een foutstructuur terugkomt
        if (
            (parsed as any[]).length > 0 &&
            (parsed as any[])[0]?.code !== undefined &&
            (parsed as any[])[0]?.code === "error"
        ) {
            parsed = [];
        }

        const newItems = parsed as ContentItemRecord[];
        const existingItems = globalState.Contentitems || [];

        // Merge strategy: Add only NEW items, skip existing ones to preserve parent content
        const merged = [...existingItems];

        for (const newItem of newItems) {
            // Get all language keys from the new item (e.g., "nl-NL", "en-GB")
            const languages = Object.keys(newItem).filter(k => k !== "length" && k !== "constructor");

            if (languages.length === 0) continue;

            // Get the name from the first language variant
            const firstLang = languages[0];
            const firstContent = newItem[firstLang];
            if (!firstContent || !firstContent.name) continue;

            const itemName = firstContent.name;

            // Check if item with same name already exists
            let existingIndex = -1;
            for (let i = 0; i < merged.length; i++) {
                const existingLangs = Object.keys(merged[i]).filter(k => k !== "length" && k !== "constructor");
                for (const lang of existingLangs) {
                    if (merged[i][lang]?.name === itemName) {
                        existingIndex = i;
                        break;
                    }
                }
                if (existingIndex >= 0) break;
            }

            if (existingIndex >= 0) {
                // Item already exists - SKIP to preserve parent content
                // Do nothing - parent version takes precedence
            } else {
                // Add complete new item with all language variants
                merged.push(newItem);
            }
        }

        // Update state with merged items and mark file as loaded
        updateGlobalState({
            Contentitems: merged,
            LoadedContentFiles: [...loadedFiles, filename]
        });

        return merged;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("mergeContentitems failed:", err);
        return globalState.Contentitems || [];
    }
}

/** Idem, maar uit Globalcontent. */
export function getGlobalContentitem(
    id: string,
    globalState: GlobalState
): LocalizedContentItem {
    try {
        const language = globalState.Language;
        const contentitems = globalState.Globalcontent ?? [];

        for (let i = 0; i < contentitems.length; i++) {
            const localized = contentitems[i]?.[language];
            if (localized && localized.name === id) {
                const sanitized = DOMPurify.sanitize(localized.content, {
                    USE_PROFILES: { html: true },
                });
                return { ...localized, content: sanitized };
            }
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("getGlobalContentitem failed:", err);
    }

    return {
        content: "",
        contentlabel: "",
        language: "",
        name: "",
        publicationStartdate: "0001-01-01T00:00:00",
        publicationEnddate: "0001-01-01T00:00:00",
        title: "",
        titleLabel: "",
    };
}

// --- html-react-parser options --------------------------------

/**
 * Opmerking:
 * - In html-react-parser is de `replace` signature: (node: DOMNode) => ReactNode
 * - We testen of het een Element is (heeft `attribs`) en vervangen conditioneel.
 * - Je originele code checkte `attribs.type === "li"/"span"`. Dat is een custom attribuut.
//   Wil je op echte HTML tags checken, gebruik dan `el.name === "li"`/`"span"`.
 */
export const options: HTMLReactParserOptions = {
    replace(node: DOMNode) {
        const el = node as Element;
        if (!("attribs" in el) || !el.attribs) return;

        // Custom attribuut 'type' behouden zoals in je JS-versie
        if (el.attribs.type === "li") {
            const props = attributesToProps(el.attribs);
            return <li {...props}>{domToReact(el.children as DOMNode[], options)}</li>;
        } else if (el.attribs.type === "span") {
            const props = attributesToProps(el.attribs);
            return (
                <span {...props}>{domToReact(el.children as DOMNode[], options)}</span>
            );
        }

        return undefined;
    },
};

// --- (optioneel) helper om HTML string te renderen -------------------------

/** Voorbeeld utility om een HTML-string te parsen met bovenstaande options. */
export function renderHtml(html: string) {
    const safe = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    return parse(safe, options);
}
